/**
 * 出金（/money/:mode/withdraw）: 金額と速さ → 手数料と手取りの見積もり → 依頼を作る → 確認ページ（パスキー）へ。
 * サーバー: GET /v1/money/balance・/passkeys、POST /v1/money/withdrawals/quote・/withdrawals（money/routes.ts・withdrawals.ts）。
 *
 * WHY 依頼を作ってから確認ページへ移るか（同じページでパスキーを出さないか）: MCP から来た出金も同じ確認ページ（/confirm/withdrawal/:token）を
 * 通る。確認の画面を 1 つにしておけば、「パスキーで確認するまでお金は動かない」の守りを 1 か所で保てる（サーバーも確認を強制する）。
 *
 * 見積もりは入力が止まって 400ms 後に取り直す（キーを打つたびに API を叩かない）。見積もりはお金を動かさない。
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../../api';
import { Empty, ErrorState, fullDate, PageHeader, Spinner } from '../../components/ui';
import { useResource } from '../../hooks';
import { errorText, useT } from '../../i18n';
import { minorDigits, money } from '../../money';
import type { BalanceView, PasskeyInfo, WithdrawalCreated, WithdrawalQuote } from '../../moneyApi';
import { amountToInput, blockReasons, confirmPagePath, confirmTokenFromUrl, liveConfirmReadiness, parseAmountInput } from '../../moneyLogic';
import { BankAccountNotice } from './BankAccountNotice';
import { useMoney } from './MoneyShell';

export function WithdrawPage() {
  const { mode, livemode } = useMoney();
  const bal = useResource<BalanceView>(`/v1/money/balance?livemode=${livemode}`);
  const keys = useResource<{ passkeys: PasskeyInfo[] }>('/v1/money/passkeys');
  const { t } = useT();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [method, setMethod] = useState<'standard' | 'instant'>('standard');
  const [quote, setQuote] = useState<WithdrawalQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 1 回の「次へ」に 1 つのキー（連打・通信断の再送で依頼が 2 つできない）。金額か速さを変えたら新しいキー
  const idem = useRef(newIdempotencyKey());

  const cur = bal.data?.currency ?? 'usd';
  const digits = minorDigits(cur);
  const amount = useMemo(() => parseAmountInput(input, digits), [input, digits]);
  const withdrawable = bal.data?.balances.withdrawable ?? 0;
  const tooMuch = amount !== null && amount > withdrawable;

  useEffect(() => {
    idem.current = newIdempotencyKey();
    setQuote(null);
    setErr(null);
    if (!amount || tooMuch || !bal.data?.payee.canWithdraw) return;
    let alive = true;
    setQuoting(true);
    const h = window.setTimeout(() => {
      api<WithdrawalQuote>('/v1/money/withdrawals/quote', { method: 'POST', body: { amount, method, currency: cur, livemode } })
        .then((q) => alive && setQuote(q))
        .catch((e: unknown) => alive && setErr(errorText(e as ApiError)))
        .finally(() => alive && setQuoting(false));
    }, 400);
    return () => {
      alive = false;
      window.clearTimeout(h);
    };
  }, [amount, method, tooMuch, cur, livemode, bal.data?.payee.canWithdraw]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!amount || !quote || quote.net < quote.minimum) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<WithdrawalCreated>('/v1/money/withdrawals', {
        method: 'POST',
        headers: { 'idempotency-key': idem.current },
        body: { amount, method, currency: cur, livemode, requestedVia: 'dashboard' }
      });
      // 確認 URL はサーバーの KUMO_DASHBOARD_URL で組まれる。同じダッシュボードの中で移る（別のオリジンで開き直さない）ためにトークンだけ使い、
      // 画面の URL ではフラグメントに置く（confirmPagePath・SEC-ACCESS-23。パスに置くと静的配信のログ・履歴に残る）
      const token = confirmTokenFromUrl(r.confirmUrl);
      if (token) navigate(confirmPagePath(token));
      else window.location.assign(r.confirmUrl);
    } catch (e2) {
      setErr(errorText(e2 as ApiError));
      setBusy(false);
    }
  };

  const loading = (bal.loading && !bal.data) || (keys.loading && !keys.data);
  const noPasskey = keys.data && keys.data.passkeys.length === 0;
  const reasons = blockReasons(bal.data?.payee.reasons ?? []);
  // live の出金はパスキーの冷却が明けてから確認できる。依頼を作る前に「いつから」を出す（依頼は作れる = 15 分の期限内に明けるなら確認できる）
  const readiness = keys.data && livemode ? liveConfirmReadiness(keys.data.passkeys, new Date()) : null;

  return (
    <>
      <PageHeader eyebrow={t(`money.mode.${mode}`)} title={t('wd.title')} />
      <p className="page-lead muted">{t('wd.lead')}</p>
      {loading && <Spinner />}
      {bal.error && <ErrorState error={bal.error} what={t('wd.title')} onRetry={bal.reload} />}

      {bal.data && !bal.data.payee.canWithdraw && (
        <section className="card">
          <Empty title={t('wd.notReadyTitle')} icon="lock">
            <p>{reasons.map((r) => t(`wd.reason.${r}`)).join(' · ')}</p>
            <Link to={`/money/${mode}/get-paid`} className="btn btn-primary">
              {t('wd.goSetup')}
            </Link>
          </Empty>
        </section>
      )}

      {bal.data?.payee.canWithdraw && noPasskey && livemode && (
        <section className="card">
          <Empty title={t('wd.noPasskeyTitle')} icon="lock">
            <p>{t('wd.noPasskeyBody')}</p>
            <Link to="/money/passkeys" className="btn btn-primary">
              {t('wd.addPasskey')}
            </Link>
          </Empty>
        </section>
      )}

      {bal.data?.payee.canWithdraw && readiness?.state === 'later' && (
        <div className="alert alert-warn" role="note">
          {t('wd.passkeyCooldown', { when: fullDate(readiness.at.toISOString()) })}
          <Link to="/money/passkeys" className="btn btn-ghost btn-sm">
            {t('money.nav.passkeys')}
          </Link>
        </div>
      )}
      {bal.data?.payee.canWithdraw && <BankAccountNotice livemode={livemode} mode={mode} compact />}

      {bal.data?.payee.canWithdraw && withdrawable <= 0 && !(noPasskey && livemode) && (
        <section className="card">
          <Empty title={t('wd.nothingTitle')}>
            <p>{t('wd.nothingBody')}</p>
          </Empty>
        </section>
      )}

      {bal.data?.payee.canWithdraw && withdrawable > 0 && !(noPasskey && livemode) && (
        <form className="card withdraw-card" onSubmit={submit}>
          <div className="withdraw-available">
            <span className="tile-label">{t('wd.available')}</span>
            <span className="tile-value">{money(withdrawable, cur)}</span>
          </div>

          <label className="field">
            <span>{t('wd.amount')}</span>
            <div className="amount-input">
              <span className="amount-prefix" aria-hidden="true">
                {cur.toUpperCase()}
              </span>
              <input
                inputMode="decimal"
                autoComplete="off"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={amountToInput(withdrawable, digits)}
                aria-invalid={input !== '' && (amount === null || tooMuch)}
                autoFocus
              />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setInput(amountToInput(withdrawable, digits))}>
                {t('wd.all')}
              </button>
            </div>
            {input !== '' && amount === null && <small className="field-error">{t('wd.invalid')}</small>}
            {tooMuch && <small className="field-error">{t('wd.tooMuch', { amount: money(withdrawable, cur) })}</small>}
          </label>

          <fieldset className="choice-group">
            <legend>{t('wd.speed')}</legend>
            {(['standard', 'instant'] as const).map((m) => (
              <label key={m} className={`choice ${method === m ? 'is-active' : ''}`}>
                <input type="radio" name="method" value={m} checked={method === m} onChange={() => setMethod(m)} />
                <span>
                  <strong>{t(`wd.${m}`)}</strong>
                  <small className="muted">{t(`wd.${m}Hint`)}</small>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="quote" aria-live="polite">
            {quoting && <p className="muted small">{t('wd.quoting')}</p>}
            {quote && !quoting && (
              <dl className="kv kv-compact">
                <div className="kv-row">
                  <dt>{t('wd.amount')}</dt>
                  <dd>{money(quote.amount, cur)}</dd>
                </div>
                {(['payout', 'instant', 'crossBorder', 'account'] as const)
                  .filter((k) => quote.fee[k] > 0)
                  .map((k) => (
                    <div className="kv-row muted" key={k}>
                      <dt>{t(`wd.fee.${k}`)}</dt>
                      <dd>−{money(quote.fee[k], cur)}</dd>
                    </div>
                  ))}
                <div className="kv-row quote-net">
                  <dt>{t('wd.youReceive')}</dt>
                  <dd>{money(Math.max(0, quote.net), cur)}</dd>
                </div>
              </dl>
            )}
            {quote && quote.net < quote.minimum && <p className="alert alert-warn">{t('wd.belowFee')}</p>}
            <p className="muted small">{t('wd.feeNote')}</p>
          </div>

          {err && (
            <p className="alert alert-error" role="alert">
              {err}
            </p>
          )}
          <div className="form-actions withdraw-actions">
            <span className="muted small">{t('wd.continueHint')}</span>
            <button className="btn btn-primary" disabled={busy || !quote || quoting || quote.net < quote.minimum}>
              {busy ? t('common.working') : t('wd.continue')}
            </button>
          </div>
        </form>
      )}
      {bal.data?.payee.canWithdraw && <p className="muted small">{t('wd.mcpNote')}</p>}
    </>
  );
}
